import { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChatMarkdown } from '../../components/chat-markdown';
import AppBar from '../../components/AppBar';
import ContentGrid from '../../components/ContentGrid';
import { GlobalMicroButton } from '../../components/GlobalMicroButton';
import LocalLLMTrust from '../../components/LocalLLMTrust';
import { useUserProfile } from '../../hooks/useUserProfile';
import { LANGUAGE_TO_FLAG_MAP, LANGUAGE_NAMES } from '../../utils/languages';
import ThesconAiScopePanel, {
  DEFAULT_SCOPE,
  type ThesconAiScopeConfig,
  type ThesconAiScopePanelHandle,
} from '../../components/ThesconAiScopePanel';
import { useThesconAiChat } from '../../hooks/useThesconAiChat';
import { useThesconAiThreadsList } from '../../hooks/useThesconAiThreadsList';
import { useThesconAiThinkingActivity } from '../../hooks/useThesconAiThinkingActivity';
import { useRetrievalJobTimeline } from '../../hooks/useRetrievalJobTimeline';
import { RetrievalJobTimeline } from '../../components/RetrievalJobTimeline';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useTheme } from '../../context/ThemeProvider';
import { getProjectById } from '../project/[projectId]/controller';
import { Favicon } from '../../utils/favicon';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveThreadScope } from '../../services/thesconAiThreads';
import styles from './page.module.css';
import { buildThesconAiPath } from '../../utils/thesconAiNavigation';
import { normalizeMarkdownLineBreaks } from '../../utils/markdownChatNormalize';

function ThesconAiRetrievalTimelineStrip({ jobId, active }: { jobId: string; active: boolean }) {
  const { job, events } = useRetrievalJobTimeline(jobId);
  return <RetrievalJobTimeline job={job} events={events} active={active} />;
}

export default function ThesconAiPage() {
  const parseIdsParam = (params: URLSearchParams, key: string): string[] => {
    const raw = params.get(key);
    if (!raw) return [];
    return raw
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { t } = useLocale();
  const { language: userLanguage, user } = useUserProfile();
  const threadIdFromUrl = searchParams.get('threadId');
  const threadsList = useThesconAiThreadsList(user?.uid);

  const setThreadIdParam = useCallback((id: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('threadId', id);
        else next.delete('threadId');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);
  const [scopeConfig, setScopeConfig] = useState<ThesconAiScopeConfig>(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      ...DEFAULT_SCOPE,
      projectId: params.get('projectId') ?? '',
      companyId: params.get('companyId') ?? '',
      knowledgeId: params.get('knowledgeId') ?? '',
      selectedProjectDocumentIds: parseIdsParam(params, 'projectDocumentIds'),
      selectedCompanyDocumentIds: parseIdsParam(params, 'companyDocumentIds'),
      selectedKnowledgeDocumentIds: parseIdsParam(params, 'knowledgeDocumentIds'),
    };
  });
  const [projectName, setProjectName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState<string | null>(null);
  const [threadsSidebarOpen, setThreadsSidebarOpen] = useState(false);

  // Fetch project and company when projectId is set
  useEffect(() => {
    if (!scopeConfig.projectId) {
      setProjectName('');
      setCompanyWebsite(null);
      return;
    }
    let cancelled = false;
    getProjectById(scopeConfig.projectId, t)
      .then(async (project) => {
        if (cancelled || !project) return;
        setProjectName(project.projectName || '');
        if (project.companyId) {
          try {
            const companyRef = doc(db, 'companies', project.companyId);
            const companyDoc = await getDoc(companyRef);
            if (!cancelled && companyDoc.exists()) {
              const data = companyDoc.data();
              setCompanyWebsite(data.website || null);
            }
          } catch {
            if (!cancelled) setCompanyWebsite(null);
          }
        } else {
          setCompanyWebsite(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProjectName('');
          setCompanyWebsite(null);
        }
      });
    return () => { cancelled = true; };
  }, [scopeConfig.projectId, t]);

  // Hydrate scope from persisted thread (authoritative when threadId is present)
  useEffect(() => {
    if (!threadIdFromUrl || !user?.uid) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'threads', threadIdFromUrl)).then((snap) => {
      if (cancelled || !snap.exists()) return;
      const data = snap.data();
      if (data.ownerId !== user.uid) return;
      const stored = data.scope as ThesconAiScopeConfig;
      setScopeConfig({ ...DEFAULT_SCOPE, ...stored });
    });
    return () => {
      cancelled = true;
    };
  }, [threadIdFromUrl, user?.uid]);

  // Sync URL query params (?projectId=, …) → scope when URL changes externally — skip while a thread owns scope
  useEffect(() => {
    if (searchParams.get('threadId')) return;

    const projectId = searchParams.get('projectId') ?? '';
    const companyId = searchParams.get('companyId') ?? '';
    const knowledgeId = searchParams.get('knowledgeId') ?? '';
    const selectedProjectDocumentIds = parseIdsParam(searchParams, 'projectDocumentIds');
    const selectedCompanyDocumentIds = parseIdsParam(searchParams, 'companyDocumentIds');
    const selectedKnowledgeDocumentIds = parseIdsParam(searchParams, 'knowledgeDocumentIds');
    setScopeConfig((prev) => ({
      ...prev,
      projectId,
      companyId,
      knowledgeId,
      selectedProjectDocumentIds: projectId
        ? (selectedProjectDocumentIds.length > 0 ? selectedProjectDocumentIds : [])
        : [],
      selectedCompanyDocumentIds: companyId
        ? (selectedCompanyDocumentIds.length > 0 ? selectedCompanyDocumentIds : [])
        : [],
      selectedKnowledgeDocumentIds: knowledgeId
        ? (selectedKnowledgeDocumentIds.length > 0 ? selectedKnowledgeDocumentIds : [])
        : [],
    }));
  }, [searchParams]);

  // Sync scope config → URL when project/knowledge changes (keeps URL shareable)
  const handleScopeChange = useCallback(
    async (next: ThesconAiScopeConfig) => {
      setScopeConfig(next);
      if (threadIdFromUrl && user?.uid) {
        try {
          await saveThreadScope(db, threadIdFromUrl, next);
        } catch (e) {
          console.warn('[thescon-ai] failed to persist scope', e);
        }
      }
      const path = buildThesconAiPath({
        threadId: threadIdFromUrl ?? undefined,
        projectId: next.projectId || undefined,
        companyId: next.companyId || undefined,
        knowledgeId: next.knowledgeId || undefined,
        projectDocumentIds: next.selectedProjectDocumentIds,
        companyDocumentIds: next.selectedCompanyDocumentIds,
        knowledgeDocumentIds: next.selectedKnowledgeDocumentIds,
      });
      const nextQuery = path.includes('?') ? path.slice(path.indexOf('?')) : '';
      setSearchParams(new URLSearchParams(nextQuery), { replace: true });
    },
    [setSearchParams, threadIdFromUrl, user?.uid]
  );
  const {
    messages,
    input,
    setInput,
    sendMessage,
    isThinking,
    clearMessages,
    documentsWithoutChunks,
  } = useThesconAiChat(scopeConfig, {
    threadId: threadIdFromUrl,
    onThreadIdChange: setThreadIdParam,
  });
  const hasRagScope = !!(scopeConfig.projectId || scopeConfig.knowledgeId || scopeConfig.companyId);
  const thinkingActivityLines = useThesconAiThinkingActivity(scopeConfig, isThinking && !hasRagScope, t);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scopePanelRef = useRef<ThesconAiScopePanelHandle>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, isThinking, thinkingActivityLines.length, hasRagScope]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      sendMessage();
      scopePanelRef.current?.close();
    },
    [sendMessage]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
        scopePanelRef.current?.close();
      }
    },
    [sendMessage]
  );

  const closeThreadsSidebar = useCallback(() => setThreadsSidebarOpen(false), []);

  useEffect(() => {
    if (!threadsSidebarOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [threadsSidebarOpen]);

  useEffect(() => {
    if (!threadsSidebarOpen) return undefined;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setThreadsSidebarOpen(false);
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [threadsSidebarOpen]);

  const pathForThesconScopeOnly = useCallback(
    () =>
      buildThesconAiPath({
        projectId: scopeConfig.projectId || undefined,
        companyId: scopeConfig.companyId || undefined,
        knowledgeId: scopeConfig.knowledgeId || undefined,
        projectDocumentIds: scopeConfig.selectedProjectDocumentIds,
        companyDocumentIds: scopeConfig.selectedCompanyDocumentIds,
        knowledgeDocumentIds: scopeConfig.selectedKnowledgeDocumentIds,
      }),
    [scopeConfig]
  );

  const handleThreadsSidebarHome = useCallback(() => {
    navigate('/home');
    closeThreadsSidebar();
  }, [navigate, closeThreadsSidebar]);

  const handleThreadsSidebarNewChat = useCallback(() => {
    navigate(pathForThesconScopeOnly());
    closeThreadsSidebar();
  }, [navigate, pathForThesconScopeOnly, closeThreadsSidebar]);

  const handleThreadsSidebarPickThread = useCallback(
    (threadId: string) => {
      navigate(buildThesconAiPath({ threadId }));
      closeThreadsSidebar();
    },
    [navigate, closeThreadsSidebar]
  );

  const isEmpty = messages.length === 0 && !isThinking;
  const canSend = input.trim().length > 0 && !isThinking;
  const pageTitle = scopeConfig.projectId && projectName
    ? `${projectName} Agent`
    : (t('thescon_ai_title') || 'Thescon Agent');
  const introMessage = scopeConfig.projectId
    ? (t('thescon_ai_intro_project') || 'Ask questions about this project. The AI will search through project documents to provide answers.')
    : (t('thescon_ai_intro') || 'AI assistant for compliance and documentation workflows.');

  return (
    <div>
      <AppBar />
      <ContentGrid rows="64px 1fr">
        {/* Toolbar left – burger (chat history) + title */}
        <div className={`${styles.toolbarLeft} ${user?.uid ? styles.toolbarLeftWithBurger : ''}`}>
          {user?.uid ? (
            <div className={styles.threadsBurger}>
              <GlobalMicroButton
                title={t('thescon_ai_chat_history')}
                icon={
                  <img
                    src={isDarkMode ? '/assets/icons/list.triangle.svg' : '/assets/icons/list.triangle.black.svg'}
                    alt=""
                    width={16}
                    height={16}
                  />
                }
                onClick={() => setThreadsSidebarOpen(true)}
              />
            </div>
          ) : null}
          <b className={styles.toolbarTitle}>{pageTitle}</b>
        </div>

        {user?.uid && threadsSidebarOpen ? (
          <>
            <div
              className={styles.threadsSidebarOverlay}
              onClick={closeThreadsSidebar}
              aria-hidden="true"
            />

            <aside className={styles.threadsSidebarPanel} aria-label={t('thescon_ai_chat_history')}>
              <div className={styles.threadsSidebarHeader}>
                <h2 className={styles.threadsSidebarHeadline}>{t('thescon_ai_chat_history')}</h2>
                <GlobalMicroButton
                  onClick={closeThreadsSidebar}
                  aria-label={t('navigation_sidebar_close') || 'Close'}
                  icon={
                    <img
                      src={isDarkMode ? '/assets/icons/xmark.svg' : '/assets/icons/xmark.black.svg'}
                      alt=""
                      width={12}
                      height={12}
                    />
                  }
                />
              </div>

              <div className={styles.threadsSidebarContent}>
                <div className={styles.threadsSidebarMicroStack}>
                  <GlobalMicroButton
                    type="button"
                    variant="primary"
                    className={styles.threadsSidebarMicroButton}
                    title={t('thescon_ai_sidebar_go_home')}
                    icon={
                      <img
                        src={isDarkMode ? '/assets/icons/chevron.left.circle.svg' : '/assets/icons/chevron.left.circle.black.svg'}
                        alt=""
                        width={14}
                        height={14}
                      />
                    }
                    onClick={handleThreadsSidebarHome}
                  >
                    {t('thescon_ai_sidebar_go_home')}
                  </GlobalMicroButton>

                  <GlobalMicroButton
                    type="button"
                    variant="primary"
                    className={styles.threadsSidebarMicroButton}
                    title={t('thescon_ai_new_chat')}
                    icon={
                      <img
                        src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
                        alt=""
                        width={14}
                        height={14}
                      />
                    }
                    onClick={handleThreadsSidebarNewChat}
                  >
                    {t('thescon_ai_new_chat')}
                  </GlobalMicroButton>
                </div>

                <nav className={styles.threadsSidebarNav} aria-label={t('thescon_ai_previous_chats')}>
                  {threadsList.length === 0 ? (
                    <p className={styles.threadsSidebarEmpty}>{t('thescon_ai_no_threads')}</p>
                  ) : (
                    threadsList.map((th) => (
                      <button
                        key={th.id}
                        type="button"
                        className={`${styles.threadsSidebarThreadItem} ${
                          threadIdFromUrl === th.id ? styles.threadsSidebarThreadItemActive : ''
                        }`}
                        onClick={() => handleThreadsSidebarPickThread(th.id)}
                      >
                        <span className={styles.threadsSidebarThreadTitle}>{th.title}</span>
                        {th.lastMessagePreview ? (
                          <span className={styles.threadsSidebarThreadPreview}>{th.lastMessagePreview}</span>
                        ) : null}
                      </button>
                    ))
                  )}
                </nav>
              </div>
            </aside>
          </>
        ) : null}

        {/* Toolbar right – delete (left) + language (far right) */}
        <div className={styles.toolbarRight}>
          {!isEmpty && (
            <GlobalMicroButton
              destructive
              icon={<img src="/assets/icons/trash.red.svg" alt="" width={12} height={12} />}
              onClick={clearMessages}
              aria-label={t('ask_archive_sheet_clear_conversation')}
            >
              {t('ask_archive_sheet_clear_button')}
            </GlobalMicroButton>
          )}
          <GlobalMicroButton
            title={t('settings_language_button_hint')}
            icon={
              <img
                src={`https://cdn.ipregistry.co/flags/emojitwo/${LANGUAGE_TO_FLAG_MAP[userLanguage] || 'gb'}.svg`}
                alt=""
                width={16}
                height={16}
              />
            }
            onClick={() => navigate('/settings?tab=languages')}
            aria-label={t('settings_language_button_text')}
          >
            {LANGUAGE_NAMES[userLanguage] || userLanguage?.toUpperCase() || 'EN'}
          </GlobalMicroButton>
        </div>

        {/* Main content: empty state or messages + input */}
        <div className={styles.chatContent}>
          {isEmpty ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateHeader}>
                {scopeConfig.projectId && companyWebsite && (
                  <div className={styles.emptyStateIcon}>
                    <Favicon websiteUrl={companyWebsite} size={32} retinaMultiplier={2} />
                  </div>
                )}
                <h1 className={styles.emptyTitle}>{pageTitle}</h1>
              </div>
              <p className={styles.emptySubtitle}>
                {introMessage}
              </p>
            </div>
          ) : (
            <div className={styles.messageList} ref={listRef} role="log" aria-live="polite">
              {messages.map((msg, msgIndex) => {
                const timelineActive =
                  !!msg.isUser &&
                  !!msg.retrievalJobId &&
                  isThinking &&
                  msgIndex === messages.length - 1;
                return (
                  <div
                    key={msg.id}
                    className={`${styles.messageRow} ${
                      msg.isUser ? styles.messageRowUser : styles.messageRowAssistant
                    }`}
                  >
                    <div
                      className={`${styles.bubble} ${
                        msg.isUser ? styles.bubbleUser : styles.bubbleAssistant
                      }`}
                    >
                      {msg.isUser ? (
                        msg.text
                      ) : (
                        <ChatMarkdown>{normalizeMarkdownLineBreaks(msg.text)}</ChatMarkdown>
                      )}
                    </div>
                    {msg.isUser && msg.retrievalJobId && (
                      <ThesconAiRetrievalTimelineStrip
                        jobId={msg.retrievalJobId}
                        active={timelineActive}
                      />
                    )}
                  </div>
                );
              })}
              {isThinking && (
                <div className={styles.thinkingBlock}>
                  <div className={styles.typingRow}>
                    <div className={styles.typingDots}>
                      <span />
                      <span />
                      <span />
                    </div>
                    <span>{t('ask_archive_sheet_thinking_text')}</span>
                  </div>
                  {thinkingActivityLines.length > 0 && (
                    <ul className={styles.thinkingActivity} aria-hidden>
                      {thinkingActivityLines.map((line) => (
                        <li key={line.id} className={styles.thinkingActivityLine}>
                          {line.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={styles.inputArea}>
            <ThesconAiScopePanel
              ref={scopePanelRef}
              config={scopeConfig}
              onChange={handleScopeChange}
              disabled={isThinking}
              documentsWithoutChunks={documentsWithoutChunks}
            >
              <form className={styles.inputForm} onSubmit={handleSubmit}>
                <textarea
                  ref={inputRef}
                  className={styles.inputField}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('ask_archive_sheet_input_placeholder')}
                  rows={1}
                  disabled={isThinking}
                  aria-label={t('ask_archive_sheet_input_placeholder')}
                />
                <button
                  type="submit"
                  className={styles.sendButton}
                  disabled={!canSend}
                  aria-label={t('ask_archive_sheet_send_message')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
                  </svg>
                </button>
              </form>
            </ThesconAiScopePanel>
            <div className={styles.inputFooter}>
              <LocalLLMTrust />
            </div>
          </div>
        </div>
      </ContentGrid>
    </div>
  );
}
